import React, { memo, useMemo } from 'react';
import { ThumbsUp, ThumbsDown, FileText } from 'lucide-react';
import { type ConversationEntry } from '../types';

interface ConversationListProps {
  conversation: ConversationEntry[];
  onFeedback: (sessionId: string, rating: number) => void;
  user: any;
}

const formatResponse = (text: string) => {
  // Format personalized greeting with proper punctuation
  text = text.replace(/^(?:!*\s*)?(Hi|Hello)\s+([^,!.]+)([,.!]?\s*[^]*?)(?=\n|$)/i, (match, greeting, name, rest) => `
    <div class="glass-panel bg-slate-900/30 p-4 rounded-xl border border-blue-500/20 mb-6">
      <p class="text-lg">
        <span class="font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
          ${greeting} ${name}
        </span>
        ${rest}
      </p>
    </div>
  `);

  // Format main sections (like "During the War:", "After the War:")
  text = text.replace(/^([A-Za-z\s]+):$/gm, (match) => `
    <h2 class="text-xl font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mt-8 mb-4">
      ${match}
    </h2>
  `);

  // Format numbered steps with titles
  text = text.replace(/(\d+)\.\s*\*\*([^:*]+):\*\*([\s\S]*?)(?=(?:\n\d+\.|$))/g, (_, number, title, content) => `
    <div class="mb-8">
      <div class="flex items-start space-x-4">
        <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 flex items-center justify-center">
          <span class="text-lg font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">${number}</span>
        </div>
        <div class="flex-1 glass-panel bg-slate-900/30 p-5 rounded-xl border border-slate-700/50">
          <h3 class="text-lg font-medium bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-3">${title}</h3>
          <div class="text-slate-300 leading-relaxed space-y-3">${content.trim()}</div>
        </div>
      </div>
    </div>
  `);

  // Format bullet points with asterisk
  text = text.replace(/\* ([^*\n]+?)([,.!])?(?:\n|$)/g, (_, content, punctuation = '') => `
    <div class="flex items-start space-x-3 mb-4">
      <div class="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 mt-2 flex-shrink-0"></div>
      <div class="flex-1">
        <div class="text-slate-300 leading-relaxed">${content.trim()}${punctuation}</div>
      </div>
    </div>
  `);

  // Format key points with asterisks (like *Increased government spending:*)
  text = text.replace(/\* ([^:*]+):/g, (_, title) => `
    <div class="mb-4">
      <h4 class="text-base font-medium bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
        ${title}
      </h4>
    </div>
  `);

  // Format paragraphs (non-list items)
  text = text.replace(/^(?!\*|<)([^<\n]+?)([,.!])?$/gm, (_, content, punctuation = '') => `
    <p class="text-slate-300 leading-relaxed mb-4">${content.trim()}${punctuation}</p>
  `);

  // Format "Here's a breakdown" or similar introductory text
  text = text.replace(/(Here's\s+[^.!]+[.!])/, (match) => `
    <div class="mb-6">
      <p class="text-lg text-slate-200">${match}</p>
    </div>
  `);

  // Format "Remember" sections
  text = text.replace(/(Remember,[^.!]+[.!][^.!]+[.!])/, (match) => `
    <div class="my-6 glass-panel bg-slate-900/30 p-4 rounded-xl border border-blue-500/20">
      <p class="text-blue-400 leading-relaxed">${match}</p>
    </div>
  `);

  // Format encouraging closing messages
  text = text.replace(/(Keep up the great work,[^!]+!)/, (match) => `
    <div class="mt-8 glass-panel bg-slate-900/30 p-4 rounded-xl border border-green-500/20">
      <p class="text-emerald-400 font-medium">${match} ✨</p>
    </div>
  `);

  // Add extra spacing between sections
  text = text.replace(/\n\n/g, '</div><div class="mt-6">');

  // Wrap the entire content
  return `<div class="space-y-4">${text}</div>`;
};

const MediaContent = memo(({ media }) => {
  if (!media || media.length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      {media.map((item, index) => (
        <div key={index} className="rounded-xl overflow-hidden glass-panel">
          {item.type === 'image' ? (
            <img
              src={item.url}
              alt="Response visualization"
              className="w-full h-auto max-h-96 object-contain"
              loading="lazy"
            />
          ) : (
            <video
              src={item.url}
              controls
              className="w-full max-h-96"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      ))}
    </div>
  );
});

const ConversationItem = memo(({ entry, onFeedback, user }) => {
  const formattedResponse = useMemo(() => {
    return { __html: formatResponse(entry.response) };
  }, [entry.response]);

  return (
    <div className="space-y-6">
      <div className="glass-panel p-6 rounded-xl">
        <p className="text-sm text-slate-400 mb-2">Your question:</p>
        <p className="text-slate-100 font-medium text-lg">{entry.question}</p>
        {entry.uploadedFiles && entry.uploadedFiles.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {entry.uploadedFiles.map((file, fileIndex) => (
              <div key={fileIndex} className="glass-panel p-2 rounded-lg flex items-center space-x-2">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-300">{file.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="glass-panel rounded-xl p-6">
        <div className="prose prose-invert max-w-none">
          <div 
            className="text-slate-100 leading-relaxed"
            dangerouslySetInnerHTML={formattedResponse}
          />
          <MediaContent media={entry.media} />
        </div>
        {user && entry.id && (
          <div className="mt-6 pt-4 border-t border-slate-800/50 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onFeedback(entry.id!, 5)}
                className={`p-2 rounded-full transition-colors ${
                  entry.feedback === 5 ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-emerald-400'
                }`}
              >
                <ThumbsUp className="w-5 h-5" />
              </button>
              <button
                onClick={() => onFeedback(entry.id!, 1)}
                className={`p-2 rounded-full transition-colors ${
                  entry.feedback === 1 ? 'bg-red-500/20 text-red-400' : 'text-slate-400 hover:text-red-400'
                }`}
              >
                <ThumbsDown className="w-5 h-5" />
              </button>
            </div>
            {entry.feedbackComment && (
              <div className="text-sm text-slate-400 italic">
                "{entry.feedbackComment}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export const ConversationList = memo(({ conversation, onFeedback, user }: ConversationListProps) => {
  const visibleConversation = useMemo(() => {
    return conversation.slice(-50); // Show only last 50 messages for performance
  }, [conversation]);

  return (
    <div className="space-y-8">
      {visibleConversation.map((entry, index) => (
        <ConversationItem
          key={index}
          entry={entry}
          onFeedback={onFeedback}
          user={user}
        />
      ))}
    </div>
  );
});